import BlockIcon from '@mui/icons-material/Block'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Dialog } from '@/components/AppDialog'
import type { Theme } from '@mui/material/styles'
import { useMemo, useState } from 'react'
import { useCreateHallBlock, useHallAvailability, useHalls } from '@/api/hooks'
import { HallSlotCalendar } from './availability/HallSlotCalendar'
import { useAuthStore } from '@/auth/authStore'
import type { BookingWindow, HallAvailability } from '@/api/types'
import {
  EmptyState,
  ErrorBanner,
  formatDateTime,
  formatNumber,
  humanise,
  InlineSpinner,
  isoDate,
  PageHeader,
} from '@/components/common'

const DAY_MS = 24 * 60 * 60 * 1000

/** Same four meanings in every palette: booked, holding, blocked, lapsed. */
function windowColour(window: BookingWindow, theme: Theme) {
  if (window.bookingStatus === 'BLOCKED') return theme.palette.text.secondary
  if (window.bookingStatus === 'CONFIRMED') return theme.palette.primary.main
  return window.blocking ? theme.palette.warning.main : theme.palette.appearance.neutral
}

export function AvailabilityPage() {
  const theme = useTheme()
  const can = useAuthStore((state) => state.can)
  // The branch comes from the toolbar and applies to every screen at once.
  const outletId = useAuthStore((state) => state.activeOutletId) ?? undefined
  const [startDate, setStartDate] = useState(() => isoDate(0))
  const [days, setDays] = useState(14)
  const [blockDialog, setBlockDialog] = useState(false)
  // The calendar answers "is the evening free on the twelfth"; the timeline
  // shows how the buffers of one booking reach into the next. Both are wanted,
  // so neither replaces the other.
  const [view, setView] = useState<'CALENDAR' | 'TIMELINE'>('CALENDAR')

  const from = useMemo(() => new Date(`${startDate}T00:00:00`).toISOString(), [startDate])
  const to = useMemo(
    () => new Date(new Date(`${startDate}T00:00:00`).getTime() + days * DAY_MS).toISOString(),
    [startDate, days],
  )

  const { data: halls } = useHalls(outletId, 'HALL')
  const { data, isLoading, error } = useHallAvailability(from, to, outletId)

  const dayList = useMemo(
    () =>
      Array.from({ length: days }, (_, index) => {
        const date = new Date(`${startDate}T00:00:00`)
        date.setDate(date.getDate() + index)
        return date
      }),
    [startDate, days],
  )

  return (
    <>
      <PageHeader
        title="Hall availability"
        subtitle="Confirmed bookings, tentative holds and maintenance blocks, including setup and cleanup buffers"
        actions={
          <Stack direction="row" spacing={1}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_event, next) => next && setView(next)}
            >
              <ToggleButton value="CALENDAR">Calendar</ToggleButton>
              <ToggleButton value="TIMELINE">Timeline</ToggleButton>
            </ToggleButtonGroup>
            {can('availability.block') ? (
              <Button variant="outlined" startIcon={<BlockIcon />} onClick={() => setBlockDialog(true)}>
                Block a hall
              </Button>
            ) : null}
          </Stack>
        }
      />

      <ErrorBanner error={error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            {view === 'TIMELINE' ? (
            <>
            <TextField
              label="From"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Window"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
            </TextField>
            </>
            ) : null}

            {view === 'TIMELINE' ? (
              <Stack direction="row" spacing={1} sx={{ ml: { md: 'auto' } }}>
                <LegendSwatch colour={theme.palette.primary.main} label="Confirmed" />
                <LegendSwatch colour={theme.palette.warning.main} label="Tentative" />
                <LegendSwatch colour={theme.palette.text.secondary} label="Blocked" />
                <LegendSwatch colour={theme.palette.appearance.neutral} label="Hold expired" />
              </Stack>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {view === 'CALENDAR' ? (
        <HallSlotCalendar halls={halls ?? []} />
      ) : (
        <>
          {isLoading && <InlineSpinner />}

          {data && data.length === 0 && (
            <EmptyState
              message="No halls are visible for this outlet."
              hint="Check your outlet access, or set up a hall under Settings."
            />
          )}

          {data?.map((hall) => (
            <HallRow key={hall.hallId} hall={hall} days={dayList} startDate={startDate} />
          ))}
        </>
      )}

      <BlockDialog open={blockDialog} onClose={() => setBlockDialog(false)} halls={data ?? []} />
    </>
  )
}

function LegendSwatch({ colour, label }: { colour: string; label: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: colour }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  )
}

function HallRow({
  hall,
  days,
  startDate,
}: {
  hall: HallAvailability
  days: Date[]
  startDate: string
}) {
  const rangeStart = new Date(`${startDate}T00:00:00`).getTime()
  const rangeEnd = rangeStart + days.length * DAY_MS

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle1">{hall.hallName}</Typography>
            <Typography variant="caption" color="text.secondary">
              Seats {formatNumber(hall.seatingCapacity)}
              {hall.floatingCapacity ? ` · floating ${formatNumber(hall.floatingCapacity)}` : ''}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            label={`${hall.occupiedWindows.filter((w) => w.blocking).length} blocking window(s)`}
          />
        </Stack>

        {/* Day grid: each occupied window is drawn as a proportional bar. */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
          {days.map((day) => (
            <Box key={day.toISOString()} sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                {/* Day and month in the house order, without the year: it is a
                    column header across one week, and the year is on the
                    heading above it. */}
                {day
                  .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })
                  .replace('/', '-')}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            position: 'relative',
            height: 44,
            // Free time reads as free. The strip's whole job is showing what
            // is open, and a grey lane made an empty week look the same as a
            // full one until you read the bars. Green is unclaimed by any
            // booking colour - those are blue, amber and grey - so nothing on
            // top of it becomes harder to tell apart.
            bgcolor: (theme) =>
              alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {days.map((_, index) => (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                left: `${(index / days.length) * 100}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: 'divider',
              }}
            />
          ))}

          {hall.occupiedWindows.map((window) => {
            const start = Math.max(new Date(window.occupiedFrom).getTime(), rangeStart)
            const end = Math.min(new Date(window.occupiedTo).getTime(), rangeEnd)
            if (end <= start) return null

            const left = ((start - rangeStart) / (rangeEnd - rangeStart)) * 100
            const width = ((end - start) / (rangeEnd - rangeStart)) * 100

            return (
              <Tooltip
                key={`${window.sourceId}-${window.occupiedFrom}`}
                title={
                  <Box>
                    <Typography variant="caption" display="block" fontWeight={600}>
                      {window.reference} · {humanise(window.bookingStatus)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Event {formatDateTime(window.eventStart)} – {formatDateTime(window.eventEnd)}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Occupied {formatDateTime(window.occupiedFrom)} – {formatDateTime(window.occupiedTo)}
                    </Typography>
                    {window.guestCount ? (
                      <Typography variant="caption" display="block">
                        {formatNumber(window.guestCount)} guests
                      </Typography>
                    ) : null}
                    {!window.blocking && (
                      <Typography variant="caption" display="block">
                        Hold expired, no longer blocking
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${left}%`,
                    width: `${Math.max(width, 0.7)}%`,
                    top: 6,
                    bottom: 6,
                    bgcolor: (theme) => windowColour(window, theme),
                    borderRadius: 0.75,
                    opacity: window.blocking ? 1 : 0.55,
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}

function BlockDialog({
  open,
  onClose,
  halls,
}: {
  open: boolean
  onClose: () => void
  halls: HallAvailability[]
}) {
  const createBlock = useCreateHallBlock()
  const [hallId, setHallId] = useState('')
  const [blockType, setBlockType] = useState('MAINTENANCE')
  const [blockStart, setBlockStart] = useState('')
  const [blockEnd, setBlockEnd] = useState('')
  const [reason, setReason] = useState('')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Block a hall</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={createBlock.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A block removes the hall from sale. The request is refused if a confirmed booking already
          falls inside the period.
        </Typography>
        <Stack spacing={2}>
          <TextField select label="Hall" value={hallId} onChange={(event) => setHallId(event.target.value)}>
            {halls.map((hall) => (
              <MenuItem key={hall.hallId} value={hall.hallId}>
                {hall.hallName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Reason type"
            value={blockType}
            onChange={(event) => setBlockType(event.target.value)}
          >
            {['MAINTENANCE', 'INTERNAL_EVENT', 'RENOVATION', 'HOLIDAY'].map((value) => (
              <MenuItem key={value} value={value}>
                {humanise(value)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="From"
            type="datetime-local"
            value={blockStart}
            onChange={(event) => setBlockStart(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="datetime-local"
            value={blockEnd}
            onChange={(event) => setBlockEnd(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Notes"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={createBlock.isPending || !hallId || !blockStart || !blockEnd}
          onClick={async () => {
            await createBlock.mutateAsync({
              hallId,
              blockType,
              blockStart: new Date(blockStart).toISOString(),
              blockEnd: new Date(blockEnd).toISOString(),
              reason: reason || undefined,
            })
            onClose()
          }}
        >
          Block
        </Button>
      </DialogActions>
    </Dialog>
  )
}
